import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonProps } from '@mui/material';
import DiscardFormDialog from '../../../components/discard-form-dialog';
import { requestCloseWithUnsavedCheck } from '../../../utils/form-unsaved-close.util';
import { PATH_DASHBOARD } from '../../../routes/paths';

type Props = Omit<ButtonProps, 'onClick' | 'type'> & {
  /** When true, ask before leaving so unsaved input is not lost silently. */
  isDirty?: boolean;
};

/**
 * "Exit" for clinical-record section forms. Leaves the record WITHOUT
 * saving (same destination as Handover's exit). It is always
 * type="button", so it never submits the surrounding form.
 *
 * It renders as a secondary (outlined, neutral) action by default so it never
 * looks like the forward "Save & Next" step of the clinical journey.
 */
export default function ExitClinicalRecordButton({
  isDirty = false,
  children = 'Exit',
  variant = 'outlined',
  color = 'inherit',
  ...other
}: Props) {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const exit = useCallback(() => {
    setConfirmOpen(false);
    navigate(PATH_DASHBOARD.patients.list);
  }, [navigate]);

  const handleClick = useCallback(() => {
    requestCloseWithUnsavedCheck(isDirty, exit, () => setConfirmOpen(true));
  }, [isDirty, exit]);

  return (
    <>
      <Button type="button" variant={variant} onClick={handleClick} {...other}>
        {children}
      </Button>
      <DiscardFormDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onDiscard={exit}
        title="Exit without saving?"
        content={<>You have unsaved changes. Exit without saving them?</>}
      />
    </>
  );
}
